import { useSelector } from 'react-redux';
import styled from 'styled-components';

import { selectCampaignReactificationV1Enabled } from 'core/userContext';
import { getPreviewHTMLTemplateUrl } from 'modules/campaigns/lib/utils/template';
import {
  EmailContentDefinition,
  InAppContentDefinition,
  MessageMedium,
} from 'modules/graphql/types';
import { useEditableFieldsContext } from 'modules/templateEditor/context/EditableFieldsContext';
import { useQueryParamContext } from 'modules/templateEditor/context/QueryParamContext';
import { ActionMenu } from 'ui/actionMenu';
import {
  ActionMenuOption,
  AllowedActionMenuElements,
  MENU_ALIGNMENTS,
  MENU_DIRECTIONS,
} from 'ui/actionMenu/types';
import { BUTTON_SIZES, BUTTON_VARIANTS, Button } from 'ui/button';
import { OpenNewWindow24, Overflow } from 'ui/icons';
import { spacing } from 'ui/theme';
import { ContentModalStateType, ContentModals, useModalContext } from 'utils/contexts/modal';

import { getIsWhatsAppCampaign } from 'modules/campaigns/lib/utils/campaign';
import { AllMessageMedium } from 'modules/domains/campaign';
import {
  PREVIEW_IN_DEVICE_URL,
  REVISION_HISTORY_URL,
  SPAM_REPORT_HREF,
} from '../../../../common/constants';
import { downloadHtml } from '../../../../common/utils';
import { useTemplateQueryContext } from '../../../../context/TemplateQueryContext';
import { ConsumingApp } from '../../InStudioTemplateEditing.types';
import { useInStudioTakeoverContext } from '../../context';

const StyledActionMenu = styled(ActionMenu)`
  a {
    display: inline-flex;
    gap: ${spacing.half};
  }
`;

enum MenuOptionNames {
  OPEN_IN_NEW_TAB = 'Open in new tab',
  PREVIEW_IN_DEVICES = 'Preview in devices',
  PREVIEW_IN_NEW_TAB = 'Preview in new tab',
  SAVE_AS_NEW_TEMPLATE = 'Save as new template',
  EXPORT_HTML = 'Export html',
  REVISION_HISTORY = 'Revision history',
  SPAM_REPORT = 'Spam report',
}

export const OPEN_NEW_TAB_OPTION = 'open-new-tab-option';

export const OverflowMenu = () => {
  const campaignReactificationV1Enabled = useSelector(selectCampaignReactificationV1Enabled);
  const { medium } = useTemplateQueryContext();
  const { campaignId, templateId, view, consumingApp } = useInStudioTakeoverContext();
  const { isDirty, hasContent, liveTemplate } = useEditableFieldsContext();
  const contentData = (liveTemplate?.mediumData || {}) as
    | EmailContentDefinition
    | InAppContentDefinition;
  const isWhatsApp = getIsWhatsAppCampaign(medium as AllMessageMedium);
  const isDisabled = isDirty || !hasContent;
  const { showModal } = useModalContext<ContentModalStateType>();
  const { locale } = useQueryParamContext();

  const setViewOptions: { value: ActionMenuOption; show: boolean }[] = [
    {
      value: {
        as: AllowedActionMenuElements.Link,
        content: (
          <>
            {MenuOptionNames.OPEN_IN_NEW_TAB}
            <OpenNewWindow24 />
          </>
        ),
        id: OPEN_NEW_TAB_OPTION,
        href: campaignReactificationV1Enabled
          ? `/campaigns/${campaignId}?view=Edit&templateEditor=open`
          : `/templates/editor?templateId=${templateId}&campaignId=${campaignId}&view=${view}`,
        target: '_blank',
        isExternalLink: true,
      },
      show: true,
    },
    {
      value: {
        as: AllowedActionMenuElements.Link,
        content: (
          <>
            {MenuOptionNames.PREVIEW_IN_DEVICES}
            <OpenNewWindow24 />
          </>
        ),
        id: 'preview-in-devices-option',
        href: `${PREVIEW_IN_DEVICE_URL}?templateId=${templateId}`,
        target: '_blank',
        isExternalLink: true,
      },
      show: medium === MessageMedium.Email,
    },
    {
      value: {
        as: AllowedActionMenuElements.Link,
        content: (
          <>
            {MenuOptionNames.PREVIEW_IN_NEW_TAB}
            <OpenNewWindow24 />
          </>
        ),
        id: 'preview-in-new-tab-option',
        href: getPreviewHTMLTemplateUrl(templateId as number, locale),
        target: '_blank',
        isExternalLink: true,
      },
      show: medium === MessageMedium.Email || medium === MessageMedium.InApp,
    },
    {
      value: {
        as: AllowedActionMenuElements.Button,
        content: `${MenuOptionNames.SAVE_AS_NEW_TEMPLATE}`,
        id: 'save-as-new-template-option',
        onClick: () => showModal({ modal: ContentModals.CopyTemplate, showCopyModal: 'show' }),
      },
      show: !isWhatsApp,
    },
    {
      value: {
        as: AllowedActionMenuElements.Button,
        content: `${MenuOptionNames.EXPORT_HTML}`,
        id: 'export-html-option',
        onClick: () => downloadHtml(`template-${templateId}.html`, contentData.html),
      },
      show: medium === MessageMedium.Email || medium === MessageMedium.InApp,
    },
    {
      value: {
        as: AllowedActionMenuElements.Link,
        content: (
          <>
            {MenuOptionNames.REVISION_HISTORY}
            <OpenNewWindow24 />
          </>
        ),
        id: 'revision-history-option',
        href: `${REVISION_HISTORY_URL}?templateId=${templateId}`,
        target: '_blank',
        isExternalLink: true,
      },
      show: true,
    },
    {
      value: {
        as: AllowedActionMenuElements.Link,
        content: (
          <>
            {MenuOptionNames.SPAM_REPORT}
            <OpenNewWindow24 />
          </>
        ),
        id: 'spam-report-option',
        href: `${SPAM_REPORT_HREF}?templateId=${templateId}`,
        target: '_blank',
        isExternalLink: true,
      },
      show: medium === MessageMedium.Email,
    },
  ];

  const viewOptions =
    consumingApp === ConsumingApp.CAMPAIGN_EDITOR
      ? setViewOptions
          .filter((o) => o.show && o.value.id !== OPEN_NEW_TAB_OPTION)
          .map(({ value }) => value)
      : setViewOptions.filter((o) => o.show).map(({ value }) => value);

  return (
    <StyledActionMenu
      data-test="in-studio-action-menu"
      align={MENU_ALIGNMENTS.LEFT}
      direction={MENU_DIRECTIONS.BOTTOM}
      isDisabled={isDisabled}
      options={viewOptions}
    >
      <Button isDisabled={isDisabled} size={BUTTON_SIZES.LARGE} variant={BUTTON_VARIANTS.GHOST}>
        <Overflow />
      </Button>
    </StyledActionMenu>
  );
};

export default OverflowMenu;
